/**
 * Stroke geometry: smoothing, variable-width ribbon outlines, path building.
 *
 * A stroke is a polyline with per-point pressure. To render it with varying
 * width we build a filled ribbon outline (left/right offset curves closed
 * with round caps). The same outline is used for canvas caches, SVG export
 * and PNG export, so every output looks identical.
 */

import { Stroke, StrokePoint } from "./model";
import { Vec2 } from "./math";

export interface Ribbon {
  /** Closed outline polygon. */
  path: Vec2[];
}

/**
 * Build the outline polygon of a variable-width polyline.
 * `width(p)` returns the half-width at a given pressure 0..1.
 */
export function ribbonOutline(
  points: StrokePoint[],
  widthAt: (pressure: number, index: number) => number,
  capScale = 1,
): Ribbon {
  if (points.length === 0) return { path: [] };
  if (points.length === 1) {
    const p = points[0]!;
    const w = widthAt(p.p, 0) * capScale;
    return { path: circlePath(p, w) };
  }

  const left: Vec2[] = [];
  const right: Vec2[] = [];
  const tangents: Vec2[] = [];

  for (let i = 0; i < points.length; i++) {
    const p = points[i]!;
    const prev = points[Math.max(0, i - 1)]!;
    const next = points[Math.min(points.length - 1, i + 1)]!;
    let dx = next.x - prev.x;
    let dy = next.y - prev.y;
    const len = Math.hypot(dx, dy);
    if (len < 1e-9) {
      dx = 1;
      dy = 0;
    } else {
      dx /= len;
      dy /= len;
    }
    tangents.push({ x: dx, y: dy });
    // normal (y-down canvas: rotate dir by -90° => (dy, -dx))
    const nx = dy;
    const ny = -dx;
    const w = widthAt(p.p, i);
    left.push({ x: p.x + nx * w, y: p.y + ny * w });
    right.push({ x: p.x - nx * w, y: p.y - ny * w });
  }

  const first = points[0]!;
  const last = points[points.length - 1]!;
  const w0 = widthAt(first.p, 0) * capScale;
  const w1 = widthAt(last.p, points.length - 1) * capScale;

  const t0 = tangents[0]!;
  const t1 = tangents[tangents.length - 1]!;
  const alpha0 = Math.atan2(t0.y, t0.x);
  const alpha1 = Math.atan2(t1.y, t1.x);

  const capSegs = 16;
  const path: Vec2[] = [];

  // 1. Start cap: semi-circle from right[0] sweeping backwards around first to left[0]
  // Angle of right[0] is alpha0 + PI/2, sweeps by +PI to alpha0 + 3*PI/2 (left[0])
  for (let k = 0; k <= capSegs; k++) {
    const u = k / capSegs;
    const theta = alpha0 + Math.PI / 2 + u * Math.PI;
    path.push({
      x: first.x + Math.cos(theta) * w0,
      y: first.y + Math.sin(theta) * w0,
    });
  }

  // 2. Left side: from left[1] to left[last] (left[0] is the end of start cap)
  for (let i = 1; i < left.length; i++) {
    path.push(left[i]!);
  }

  // 3. End cap: semi-circle from left[last] sweeping forwards around last to right[last]
  // Angle of left[last] is alpha1 - PI/2, sweeps by +PI to alpha1 + PI/2 (right[last])
  for (let k = 1; k <= capSegs; k++) {
    const u = k / capSegs;
    const theta = alpha1 - Math.PI / 2 + u * Math.PI;
    path.push({
      x: last.x + Math.cos(theta) * w1,
      y: last.y + Math.sin(theta) * w1,
    });
  }

  // 4. Right side: from right[last - 1] back down to right[1]
  // (right[last] was the end of end cap, right[0] was the start of start cap)
  for (let i = right.length - 2; i >= 1; i--) {
    path.push(right[i]!);
  }

  return { path };
}

function circlePath(c: Vec2, r: number, segs = 32): Vec2[] {
  const count = Math.max(24, segs);
  const pts: Vec2[] = [];
  for (let i = 0; i < count; i++) {
    const t = (i / count) * Math.PI * 2;
    pts.push({ x: c.x + Math.cos(t) * r, y: c.y + Math.sin(t) * r });
  }
  return pts;
}

/** Add a closed polygon to a 2D context path. */
export function tracePolygon(ctx: CanvasRenderingContext2D | Path2D, pts: Vec2[]): void {
  if (pts.length < 3) return;
  ctx.moveTo(pts[0]!.x, pts[0]!.y);
  for (let i = 1; i < pts.length; i++) {
    ctx.lineTo(pts[i]!.x, pts[i]!.y);
  }
  ctx.closePath();
}

/** Average a pressure sample to a 0..1 brush pressure. */
export const pressureOf = (p: number): number => (Number.isFinite(p) ? p : 0.5);

/** Light moving-average smoothing; keeps the stroke responsive. */
export function smoothPoints(points: StrokePoint[], window = 2): StrokePoint[] {
  if (points.length < 3) return points;
  const out: StrokePoint[] = [];
  for (let i = 0; i < points.length; i++) {
    const lo = Math.max(0, i - window);
    const hi = Math.min(points.length - 1, i + window);
    let x = 0;
    let y = 0;
    let p = 0;
    for (let j = lo; j <= hi; j++) {
      x += points[j]!.x;
      y += points[j]!.y;
      p += points[j]!.p;
    }
    const n = hi - lo + 1;
    out.push({ x: x / n, y: y / n, p: p / n });
  }
  return out;
}

/** Axis-aligned bounding box of a stroke's outline (with half-width w). */
export function strokeBounds(stroke: Stroke): { minX: number; minY: number; maxX: number; maxY: number } {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const pt of stroke.points) {
    const w = strokeWidthAt(stroke, pt.p);
    minX = Math.min(minX, pt.x - w);
    minY = Math.min(minY, pt.y - w);
    maxX = Math.max(maxX, pt.x + w);
    maxY = Math.max(maxY, pt.y + w);
  }
  return { minX, minY, maxX, maxY };
}

export function strokeWidthAt(stroke: Stroke, pressure: number): number {
  // Pressure modulates width between 30% and 100% of the base size.
  const t = 0.3 + 0.7 * pressure;
  return (stroke.style.size * t) / 2;
}

/** Distance from point to the stroke's polyline center (in cell units). */
export function distanceToStroke(stroke: Stroke, p: Vec2): number {
  let best = Infinity;
  for (let i = 0; i < stroke.points.length - 1; i++) {
    const a = stroke.points[i]!;
    const b = stroke.points[i + 1]!;
    best = Math.min(best, distToSegment(p, a, b));
  }
  if (stroke.points.length === 1) {
    best = Math.hypot(p.x - stroke.points[0]!.x, p.y - stroke.points[0]!.y);
  }
  return best;
}

function distToSegment(p: Vec2, a: Vec2, b: Vec2): number {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const len2 = abx * abx + aby * aby;
  if (len2 < 1e-12) return Math.hypot(p.x - a.x, p.y - a.y);
  let t = ((p.x - a.x) * abx + (p.y - a.y) * aby) / len2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(p.x - (a.x + abx * t), p.y - (a.y + aby * t));
}

/** Serialize an outline to an SVG path `d` attribute (single closed subpath). */
export function outlineToSvgPath(pts: Vec2[]): string {
  if (pts.length === 0) return "";
  let d = `M${fmt(pts[0]!.x)} ${fmt(pts[0]!.y)}`;
  for (let i = 1; i < pts.length; i++) {
    d += ` L${fmt(pts[i]!.x)} ${fmt(pts[i]!.y)}`;
  }
  return d + " Z";
}

function fmt(n: number): string {
  return Math.abs(n) < 1e-9 ? "0" : String(Math.round(n * 1e6) / 1e6);
}

/**
 * Clip a polyline of StrokePoints against a circle (eraser head).
 * Returns an array of remaining StrokePoint polylines (0, 1, or more pieces).
 */
export function clipStrokeWithCircle(
  points: StrokePoint[],
  center: Vec2,
  radius: number,
): StrokePoint[][] {
  if (points.length === 0) return [];

  const r2 = radius * radius;

  if (points.length === 1) {
    const p = points[0]!;
    const d2 = (p.x - center.x) ** 2 + (p.y - center.y) ** 2;
    return d2 > r2 ? [[p]] : [];
  }

  const pieces: StrokePoint[][] = [];
  let currentPiece: StrokePoint[] = [];

  const first = points[0]!;
  const firstD2 = (first.x - center.x) ** 2 + (first.y - center.y) ** 2;
  if (firstD2 > r2) {
    currentPiece.push(first);
  }

  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i]!;
    const b = points[i + 1]!;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const segLen2 = dx * dx + dy * dy;

    if (segLen2 < 1e-12) {
      continue;
    }

    const fx = a.x - center.x;
    const fy = a.y - center.y;

    const quadA = segLen2;
    const quadB = 2 * (fx * dx + fy * dy);
    const quadC = fx * fx + fy * fy - r2;

    const disc = quadB * quadB - 4 * quadA * quadC;

    if (disc <= 0) {
      if (quadC <= 0) {
        if (currentPiece.length > 0) {
          pieces.push(currentPiece);
          currentPiece = [];
        }
      } else {
        if (currentPiece.length === 0) {
          currentPiece.push(a);
        }
        currentPiece.push(b);
      }
      continue;
    }

    const sqrtDisc = Math.sqrt(disc);
    const t1 = (-quadB - sqrtDisc) / (2 * quadA);
    const t2 = (-quadB + sqrtDisc) / (2 * quadA);

    const enter = Math.max(0, t1);
    const exit = Math.min(1, t2);

    if (enter >= exit) {
      if (currentPiece.length === 0) {
        currentPiece.push(a);
      }
      currentPiece.push(b);
      continue;
    }

    // Subsegment [enter, exit] is inside circle
    if (enter > 1e-5) {
      const enterPt: StrokePoint = {
        x: a.x + enter * dx,
        y: a.y + enter * dy,
        p: a.p + enter * (b.p - a.p),
      };
      if (currentPiece.length === 0) {
        currentPiece.push(a);
      }
      currentPiece.push(enterPt);
      pieces.push(currentPiece);
      currentPiece = [];
    } else {
      if (currentPiece.length > 0) {
        pieces.push(currentPiece);
        currentPiece = [];
      }
    }

    if (exit < 1 - 1e-5) {
      const exitPt: StrokePoint = {
        x: a.x + exit * dx,
        y: a.y + exit * dy,
        p: a.p + exit * (b.p - a.p),
      };
      currentPiece = [exitPt, b];
    } else {
      currentPiece = [];
    }
  }

  if (currentPiece.length > 0) {
    pieces.push(currentPiece);
  }

  return pieces.filter((piece) => {
    if (piece.length === 0) return false;
    if (piece.length === 1) return points.length === 1;
    let totalLen = 0;
    for (let j = 0; j < piece.length - 1; j++) {
      totalLen += Math.hypot(piece[j + 1]!.x - piece[j]!.x, piece[j + 1]!.y - piece[j]!.y);
    }
    return totalLen > 1e-5;
  });
}
